import { useCallback, useRef, useState } from 'react'
import TRTC from 'trtc-sdk-v5'

function waitForElement(id, attempts = 30) {
  return new Promise((resolve, reject) => {
    const tick = (left) => {
      if (document.getElementById(id)) {
        resolve()
        return
      }
      if (left <= 0) {
        reject(new Error(`Video view #${id} not ready`))
        return
      }
      requestAnimationFrame(() => tick(left - 1))
    }
    tick(attempts)
  })
}

function isLeaveRoomError(err) {
  const msg = String(err?.message || err || '')
  return /leave room|connection aborted/i.test(msg)
}

export function useTrtcCall() {
  const trtcRef = useRef(null)
  const joinSeqRef = useRef(0)
  const [callState, setCallState] = useState(null)
  const [muted, setMuted] = useState(false)
  const [cameraOff, setCameraOff] = useState(false)

  const cleanupTrtc = useCallback(async () => {
    const trtc = trtcRef.current
    if (!trtc) return
    trtcRef.current = null
    try {
      await trtc.exitRoom()
    } catch {
      /* ignore */
    }
    try {
      trtc.destroy()
    } catch {
      /* ignore */
    }
  }, [])

  const endCall = useCallback(async () => {
    joinSeqRef.current += 1
    await cleanupTrtc()
    setCallState(null)
    setMuted(false)
    setCameraOff(false)
  }, [cleanupTrtc])

  const joinCall = useCallback(async ({
    sdkAppId,
    userId,
    userSig,
    strRoomId,
    mode,
    peerName,
    callId,
    isIncoming = false,
  }) => {
    const seq = ++joinSeqRef.current
    const aborted = () => seq !== joinSeqRef.current

    await cleanupTrtc()
    if (aborted()) return

    const trtc = TRTC.create()
    trtcRef.current = trtc

    setCallState({
      status: 'connecting',
      mode: mode === 'voice' ? 'voice' : 'video',
      peerName: peerName || 'Contact',
      strRoomId,
      callId,
      isIncoming,
    })

    // Let React mount video containers before TRTC binds to #trtc-local-view.
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)))
    if (aborted()) return

    trtc.on(TRTC.EVENT.REMOTE_VIDEO_AVAILABLE, ({ userId: remoteUserId, streamType }) => {
      if (aborted()) return
      trtc.startRemoteVideo({
        userId: remoteUserId,
        streamType,
        view: 'trtc-remote-view',
      }).catch(() => {})
    })

    trtc.on(TRTC.EVENT.REMOTE_USER_LEAVE, () => {
      setCallState((prev) => {
        if (!prev || prev.status !== 'active') return prev
        return { ...prev, status: 'ended', endReason: 'peer_left' }
      })
    })

    trtc.on(TRTC.EVENT.KICKED_OUT, () => {
      setCallState((prev) => (prev ? { ...prev, status: 'ended', endReason: 'kicked' } : prev))
    })

    try {
      await trtc.enterRoom({
        strRoomId,
        scene: 'rtc',
        sdkAppId: Number(sdkAppId),
        userId: String(userId),
        userSig,
        autoReceiveAudio: true,
        autoReceiveVideo: mode !== 'voice',
      })
      if (aborted()) return

      await trtc.startLocalAudio()
      if (aborted()) return

      if (mode !== 'voice') {
        await waitForElement('trtc-local-view')
        if (aborted()) return
        await trtc.startLocalVideo({ view: 'trtc-local-view' })
        if (aborted()) return
      }

      setCallState((prev) => (prev ? { ...prev, status: 'active' } : prev))
    } catch (err) {
      if (aborted() || isLeaveRoomError(err)) return
      await cleanupTrtc()
      setCallState(null)
      throw err
    }
  }, [cleanupTrtc])

  const toggleMute = useCallback(async () => {
    const trtc = trtcRef.current
    if (!trtc) return
    const next = !muted
    if (next) await trtc.muteLocalAudio()
    else await trtc.unmuteLocalAudio()
    setMuted(next)
  }, [muted])

  const toggleCamera = useCallback(async () => {
    const trtc = trtcRef.current
    if (!trtc || callState?.mode === 'voice') return
    const next = !cameraOff
    if (next) await trtc.stopLocalVideo()
    else await trtc.startLocalVideo({ view: 'trtc-local-view' })
    setCameraOff(next)
  }, [cameraOff, callState?.mode])

  return {
    callState,
    muted,
    cameraOff,
    joinCall,
    endCall,
    toggleMute,
    toggleCamera,
    setCallState,
  }
}
